import { useState, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Loader2 } from 'lucide-react';
import type { SchoolViewExtra } from '@/types/api';
import { SetupStep1ScheduleType, type Step1Ref } from './setup-wizard/setup-step-1-schedule-type';
import { SetupStep2Calendar, type Step2Ref } from './setup-wizard/setup-step-2-calendar';
import { SetupStep3Schedules, type Step3Ref } from './setup-wizard/setup-step-3-schedules';
import { useUpdateSchool, useSchool } from '@/hooks/use-schools';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface SchoolSetupWizardProps {
  school: SchoolViewExtra;
  schoolId: string;
}

const STEPS = [
  { id: 1, title: 'Schedule Type' },
  { id: 2, title: 'Calendar' },
  { id: 3, title: 'Schedules' },
];

export function SchoolSetupWizard({ school: initialSchool, schoolId }: SchoolSetupWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const updateSchoolMutation = useUpdateSchool(schoolId, initialSchool.organizationType);
  
  // Use the hook to get live updates to school data
  const { data: liveSchool, refetch: refetchSchool } = useSchool(schoolId);
  const school = liveSchool || initialSchool;

  // Refs to call save functions on each step
  const step1Ref = useRef<Step1Ref>(null);
  const step2Ref = useRef<Step2Ref>(null);
  const step3Ref = useRef<Step3Ref>(null);

  const schoolPrimaryColor = school.primaryColor || '#1e40af';

  const getContrastColor = (hexColor: string): string => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? 'black' : 'white';
  };

  const isStepComplete = (step: number) => completedSteps.includes(step);
  
  // Check if current step can be saved (has valid data)
  const canSaveCurrentStep = () => {
    if (currentStep === 1) return step1Ref.current?.canSave() ?? false;
    if (currentStep === 2) return step2Ref.current?.canSave() ?? false;
    if (currentStep === 3) return step3Ref.current?.canSave() ?? false;
    return false;
  };

  const handleNext = async () => {
    setIsSaving(true);
    
    try {
      // Save current step
      let saved = false;
      if (currentStep === 1) {
        saved = await step1Ref.current?.save() ?? false;
      } else if (currentStep === 2) {
        saved = await step2Ref.current?.save() ?? false;
      } else if (currentStep === 3) {
        saved = await step3Ref.current?.save() ?? false;
      }

      if (!saved) {
        setIsSaving(false);
        return;
      }

      // Refetch school data immediately to ensure next step has fresh data
      await refetchSchool();

      // Mark step complete and move to next
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps(prev => [...prev, currentStep]);
      }

      if (currentStep < STEPS.length) {
        setCurrentStep(currentStep + 1);
      } else if (currentStep === STEPS.length) {
        // Final step - mark school as live
        await updateSchoolMutation.mutateAsync({
          school: {
            status: 'live',
          },
        });
        toast.success('School is now live! 🎉');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepComplete = useCallback((step: number) => {
    setCompletedSteps(prev => {
      if (!prev.includes(step)) {
        return [...prev, step];
      }
      return prev;
    });
  }, []);

  return (
    <div className="space-y-6 animate-in fade-in-0 slide-in-from-bottom-4 duration-700">
      {/* Step Indicators */}
      <div className="flex items-center justify-center gap-2">
        {STEPS.map((step, idx) => {
          const isActive = currentStep === step.id;
          const isComplete = isStepComplete(step.id);
          
          return (
            <div key={step.id} className="flex items-center">
            <button
              onClick={() => setCurrentStep(step.id)}
                className={cn(
                  "relative flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-300",
                  isActive && "scale-105"
                )}
                style={
                isActive
                    ? {
                        backgroundColor: schoolPrimaryColor,
                        color: getContrastColor(schoolPrimaryColor),
                        boxShadow: `0 4px 12px ${schoolPrimaryColor}40`
                      }
                  : isComplete
                      ? {
                          backgroundColor: `${schoolPrimaryColor}15`,
                          color: schoolPrimaryColor,
                        }
                      : {
                          backgroundColor: 'transparent',
                          border: '2px solid',
                          borderColor: `${schoolPrimaryColor}20`,
                        }
                }
            >
                <div 
                  className={cn(
                    "flex items-center justify-center w-6 h-6 rounded-full transition-all",
                    isComplete && "scale-110"
                  )}
                  style={
                    isComplete
                      ? { backgroundColor: schoolPrimaryColor, color: getContrastColor(schoolPrimaryColor) }
                      : { backgroundColor: 'transparent' }
                  }
                >
                {isComplete ? (
                    <Check className="h-4 w-4" />
                ) : (
                    <span className="text-sm font-semibold">{step.id}</span>
                  )}
                </div>
                <span className="text-sm font-medium">{step.title}</span>
              </button>
              
              {idx < STEPS.length - 1 && (
                <div 
                  className="w-8 h-0.5 mx-1"
                  style={{ 
                    backgroundColor: isStepComplete(step.id) ? schoolPrimaryColor : `${schoolPrimaryColor}20` 
                  }}
                />
              )}
              </div>
          );
        })}
      </div>

      {/* Step Content */}
      <Card className="border-2" style={{ borderColor: `${schoolPrimaryColor}20` }}>
        <CardContent className="pt-6">
          <div className={currentStep === 1 ? 'block' : 'hidden'}>
            <SetupStep1ScheduleType
              ref={step1Ref}
              school={school}
              schoolId={schoolId}
              onComplete={() => handleStepComplete(1)}
            />
          </div>
          <div className={currentStep === 2 ? 'block' : 'hidden'}>
            <SetupStep2Calendar
              ref={step2Ref}
              school={school}
              schoolId={schoolId}
              onComplete={() => handleStepComplete(2)}
            />
          </div>
          <div className={currentStep === 3 ? 'block' : 'hidden'}>
            <SetupStep3Schedules
              ref={step3Ref}
              school={school}
              schoolId={schoolId}
              onComplete={() => handleStepComplete(3)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between pt-2">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={currentStep === 1 || isSaving}
          className="min-w-[100px]"
        >
          Back
        </Button>

        <Button
          onClick={handleNext}
          disabled={isSaving}
          className="min-w-[160px]"
          style={{ 
            backgroundColor: schoolPrimaryColor,
            color: getContrastColor(schoolPrimaryColor)
          }}
        >
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : currentStep === STEPS.length ? (
            'Finish Setup'
          ) : (
            'Save & Continue'
          )}
        </Button>
      </div>
    </div>
  );
}
